import { format } from 'date-fns'
import React from 'react'

import {
  DistributionChannel,
  ReviewCategory,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { formatDateRange } from '@avantstay/format-date-range'

import { directLeadSources } from '../../constants'
import { getOtaChannelIcon, UI_DATE_FORMAT_FULL_YEAR } from '../../utils'
import { RatingBar } from '../RatingBar'
import { TruncatedTextBlock } from '../TruncatedTextBlock'
import * as S from './ReviewCard.styles'
import { ReviewComment } from './ReviewCard.styles'
import { ReviewCardProps } from './ReviewCard.types'
import { getOverallRating } from './ReviewCard.utils'

interface ReviewCardMiniProps extends ReviewCardProps {
  onSeeMore?: () => void
}

export const ReviewCardMini = ({ review, getBookingLink, onSeeMore }: ReviewCardMiniProps) => {
  const { booking, categoryReviews, source, createdAt, bookingPeriod } = review
  const detailedRatings = categoryReviews.filter(
    reviewDetail =>
      reviewDetail.category !== ReviewCategory.overall &&
      reviewDetail.category !== ReviewCategory.nps,
  )
  const isSourceAvantStay = directLeadSources.includes(source)
  const overallRating = getOverallRating({ categoryReviews, isSourceAvantStay })

  const distributionChannel: DistributionChannel = isSourceAvantStay
    ? DistributionChannel.avantstay
    : (source.replace('_', '') as DistributionChannel)

  return (
    <S.MiniCardContainer>
      {booking ? (
        <S.BookingDetails>
          <S.ChannelIcon>{getOtaChannelIcon(distributionChannel)}</S.ChannelIcon>
          <div style={{ flexGrow: 1 }}>
            <S.GuestName>{booking.guestName}</S.GuestName>
            <S.BookingPeriodDatesLink
              href={getBookingLink ? getBookingLink(booking.hash) : undefined}
            >
              {formatDateRange(bookingPeriod.start, bookingPeriod.end)}
            </S.BookingPeriodDatesLink>
          </div>
          <div>
            <S.ReviewDate>{format(new Date(createdAt), UI_DATE_FORMAT_FULL_YEAR)}</S.ReviewDate>
            {overallRating != null && (
              <RatingBar isNPS={isSourceAvantStay} isOverall rating={overallRating.rating} />
            )}
          </div>
        </S.BookingDetails>
      ) : null}
      <S.ReviewBreakdownContainer
        shouldDisplayAsColumn={true}
        style={{ overflow: 'hidden', flexGrow: 1 }}
      >
        {detailedRatings?.length > 0 && (
          <S.DetailedRatingsColumnsContainer mini={true}>
            {detailedRatings.map(reviewDetail => (
              <S.DetailedReviewWithBorder
                key={`detailedRatings-${review.id}-${reviewDetail.category}`}
                reviewDetail={reviewDetail}
                hideBadges={true}
                shouldShowMaxRating={false}
              />
            ))}
          </S.DetailedRatingsColumnsContainer>
        )}
        {overallRating?.comment ? (
          <S.ReviewCommentContainer style={{ overflow: 'hidden' }}>
            <S.ReviewCommentHeader>
              {isSourceAvantStay ? 'Private NPS Review' : 'Public Review'}
            </S.ReviewCommentHeader>
            {onSeeMore ? (
              <S.ReviewCommentTruncated>
                <TruncatedTextBlock contents={overallRating?.comment} onSeeMoreClick={onSeeMore} />
              </S.ReviewCommentTruncated>
            ) : (
              <ReviewComment>{overallRating?.comment}</ReviewComment>
            )}
          </S.ReviewCommentContainer>
        ) : (
          <S.NoComment subtitle="No review comment" />
        )}
      </S.ReviewBreakdownContainer>
    </S.MiniCardContainer>
  )
}
